import { type DscNoteResponse, GetDscNotesPhaseEnum } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchDscNotesApi } from "../../../api/client";
import { dscNotesKeys } from "./dscNotesKeys";
import { type DscChangeHistoryEntry, toChangeHistoryEntry } from "./types";

// caps2 parity: the popup always requested the full pre-approval history
// (isShowAllPreAppHist=true), so the Workbench mirrors that with phase=all.
// Filtering UI is explicitly out of scope for OR-21039.
const DEFAULT_PHASE = GetDscNotesPhaseEnum.All;

/**
 * Shared queryOptions for a deal's DSC Pre-approval Change History (key +
 * queryFn). Backed by the generated `WorkbenchDSCNotesApi.getDscNotes`, which
 * forwards to recap-service and returns the rows newest-first — the table
 * renders them in server order (no client-side sort). Rows are normalized at
 * the boundary via {@link toChangeHistoryEntry} so the render path stays free of
 * optional-field handling. `staleTime` matches the app-wide 30s convention.
 *
 * Reusable by useQuery, prefetchQuery, ensureQueryData, getQueryData, and
 * setQueryData.
 * @param {string} approvalId - The numeric approval / vehicle-loan-application id.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function dscNotesQuery(approvalId: string) {
  return queryOptions({
    queryKey: dscNotesKeys.list({ approvalId, phase: DEFAULT_PHASE }),
    queryFn: async ({ signal }): Promise<DscChangeHistoryEntry[]> => {
      const response = await workbenchDscNotesApi.getDscNotes(approvalId, DEFAULT_PHASE, { signal });
      return response.data.map((row: DscNoteResponse) => toChangeHistoryEntry(row));
    },
    staleTime: 30_000,
    enabled: !!approvalId,
  });
}

/**
 * Reads the DSC Pre-approval Change History for a deal. Disabled (never fires)
 * until the approval id is present, so callers can render an empty/loading state
 * instead of issuing a doomed `//dsc-notes` request.
 * @param {string} approvalId - The numeric approval / vehicle-loan-application id.
 * @returns {ReturnType<typeof useQuery<DscChangeHistoryEntry[]>>} The TanStack Query result.
 */
export function useDscNotes(approvalId: string) {
  return useQuery(dscNotesQuery(approvalId));
}
